import React, { useState } from "react";
import { CheckCircle2, Mail, Users, Bus } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { getGuestName, setGuestName } from "@/lib/guest";
import { toast } from "sonner";

export default function Asistencia() {
  const { t } = useI18n();
  return (
    <main data-testid="asistencia-page" className="section">
      <h1 className="page-title font-script">{t.asistencia.title}</h1>

      <ConfirmTab t={t} />
    </main>
  );
}

function ConfirmTab({ t }) {
  const [form, setForm] = useState({
    name: getGuestName(),
    attending: null,
    companion_name: "",
    children: 0,
    dietary: "",
    bus_outbound: null,
    bus_return: null,
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { toast.error(t.common.nameRequired); return; }
    if (form.attending === null) { toast.error(t.common.attendanceRequired); return; }
    setLoading(true);
    try {
      await api.post("/rsvp", {
        name: form.name.trim(),
        attending: form.attending,
        companion_name: form.companion_name || null,
        children: Number(form.children) || 0,
        dietary: form.dietary || null,
        bus_outbound: form.attending ? form.bus_outbound : null,
        bus_return: form.attending ? form.bus_return : null,
        notes: form.notes || null,
      });
      setGuestName(form.name);
      setSubmitted(true);
      toast.success(t.asistencia.thanks);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="s-card s-card-pad text-center" data-testid="confirm-thanks">
          <CheckCircle2 className="mx-auto text-[color:var(--olive)] mb-3" size={48} />
          <h3 className="font-script text-3xl text-[color:var(--ink-wine)] mb-2">{t.asistencia.thanks}</h3>
          <p className="text-[color:var(--ink-deep)] mb-4">{t.asistencia.thanksMsg}</p>
          <button
            onClick={() => {
              setSubmitted(false);
              setForm({ name: "", attending: null, companion_name: "", children: 0, dietary: "", bus_outbound: null, bus_return: null, notes: "" });
            }}
            className="btn-outline"
            data-testid="confirm-another"
          >
            {t.asistencia.another}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto space-y-5" data-testid="confirm-form">
      <div className="s-card s-card-pad">
        <div className="flex items-center gap-2 mb-4 text-[color:var(--ink-wine)]">
          <Users size={18} />
          <span className="font-script-alt text-2xl">{t.asistencia.yourAttendance}</span>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label">{t.asistencia.yourName}</label>
            <input
              data-testid="confirm-name"
              value={form.name}
              onChange={(e) => upd("name", e.target.value)}
              className="field"
              placeholder={t.asistencia.yourNamePh}
              required
            />
          </div>

          <div>
            <label className="label">{t.asistencia.willYouCome}</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="confirm-yes"
                onClick={() => upd("attending", true)}
                className={`pill-yn ${form.attending === true ? "active" : ""}`}
              >
                {t.asistencia.yes}
              </button>
              <button
                type="button"
                data-testid="confirm-no"
                onClick={() => upd("attending", false)}
                className={`pill-yn ${form.attending === false ? "active" : ""}`}
              >
                {t.asistencia.no}
              </button>
            </div>
          </div>

          {form.attending && (
            <>
              <div>
                <label className="label">{t.asistencia.companionName}</label>
                <input
                  data-testid="confirm-companion"
                  value={form.companion_name}
                  onChange={(e) => upd("companion_name", e.target.value)}
                  className="field"
                  placeholder={t.asistencia.companionPh}
                />
              </div>

              <div>
                <label className="label">{t.asistencia.children}</label>
                <div className="flex gap-3" data-testid="confirm-children">
                  {[0, 1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => upd("children", n)}
                      className={`num-chip ${form.children === n ? "active" : ""}`}
                      data-testid={`children-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">{t.asistencia.dietary}</label>
                <input
                  data-testid="confirm-dietary"
                  value={form.dietary}
                  onChange={(e) => upd("dietary", e.target.value)}
                  className="field"
                  placeholder={t.asistencia.dietaryPh}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {form.attending && (
        <div className="s-card s-card-pad" data-testid="confirm-bus-card">
          <div className="flex items-center gap-2 mb-3 text-[color:var(--ink-wine)]">
            <Bus size={18} />
            <span className="font-script-alt text-2xl">{t.asistencia.busTitle}</span>
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-4">{t.asistencia.busDesc}</p>

          <div className="mb-4">
            <label className="label">{t.asistencia.busOut}</label>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => upd("bus_outbound", true)} data-testid="bus-out-yes"
                className={`pill-yn ${form.bus_outbound === true ? "active" : ""}`}>{t.asistencia.yes}</button>
              <button type="button" onClick={() => upd("bus_outbound", false)} data-testid="bus-out-no"
                className={`pill-yn ${form.bus_outbound === false ? "active" : ""}`}>{t.asistencia.no}</button>
            </div>
          </div>

          <div className="mb-4">
            <label className="label">{t.asistencia.busRet}</label>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => upd("bus_return", true)} data-testid="bus-ret-yes"
                className={`pill-yn ${form.bus_return === true ? "active" : ""}`}>{t.asistencia.yes}</button>
              <button type="button" onClick={() => upd("bus_return", false)} data-testid="bus-ret-no"
                className={`pill-yn ${form.bus_return === false ? "active" : ""}`}>{t.asistencia.no}</button>
            </div>
          </div>

          <div>
            <label className="label">{t.asistencia.notes}</label>
            <textarea
              data-testid="confirm-notes"
              value={form.notes}
              onChange={(e) => upd("notes", e.target.value)}
              rows={3}
              className="field"
              placeholder={t.asistencia.notesPh}
            />
          </div>
        </div>
      )}

      <button type="submit" disabled={loading} className="btn-primary" data-testid="confirm-submit">
        <Mail size={16} /> {loading ? "..." : t.asistencia.submitConfirm}
      </button>
    </form>
  );
}
